import { t } from '@lingui/core/macro';
import { useState } from 'react';

import { useCampaignOptions } from '@/api/campaign';
import {
  encodeFilterFieldName,
  FilterField,
  InfiniteComboboxPickerFilter,
  InfiniteScrollCategoryFilter,
  useFilterForm,
} from '@/components/filters';
import type { FilterOperatorMulti } from '@/components/InstantSearch';
import { usePageFilterContext } from '@/components/page';
import type { FilterComponentProps } from '@/providers/TableState';

const PAGE_SIZE = 50;

type Props = FilterComponentProps & {
  channelId?: string;
  mode?: 'multiple';
  hasExclude?: boolean;
};

const CampaignFilter = ({
  name,
  label,
  infoPopover,
  compact = false,
  mode = 'multiple',
  hasExclude = true,
  channelId,
}: Props) => {
  const form = useFilterForm();
  const [search, setSearch] = useState<string>('');
  const {
    pageFilters: { startDate, endDate },
  } = usePageFilterContext();

  const { campaignOptions, error, hasMore, isLoading, isFetchingNextPage, fetchNextPage } =
    useCampaignOptions({ search, pageSize: PAGE_SIZE, channelId, startDate, endDate });

  return (
    <form.AppField name={encodeFilterFieldName(name)}>
      {(field) => (
        <FilterField
          data-testid="campaign-filter"
          label={label}
          infoPopover={infoPopover}
          hideLabel={!compact}
        >
          {compact ? (
            <InfiniteComboboxPickerFilter
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              placeholder={t`Select Campaigns...`}
              loadingPlaceholder={t`Loading Campaigns...`}
              options={campaignOptions}
              isLoading={isLoading}
              isLoadingMore={isFetchingNextPage}
              hasMore={hasMore}
              error={error}
              loadMore={fetchNextPage}
              notFoundContent={t`No campaigns were found...`}
              hasExclude={hasExclude}
              onSearch={setSearch}
            />
          ) : (
            <InfiniteScrollCategoryFilter
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              selectionMode={mode}
              options={campaignOptions}
              isLoading={isLoading}
              isLoadingMore={isFetchingNextPage}
              hasMore={hasMore}
              error={error}
              loadMore={fetchNextPage}
              searchText={search}
              onSearch={setSearch}
            />
          )}
        </FilterField>
      )}
    </form.AppField>
  );
};

export default CampaignFilter;
